import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Package, LogOut, Bell, Plus, Trash2, Lock, Megaphone, UploadCloud, Tag
} from 'lucide-react';

import {
  collection, query, orderBy, onSnapshot, doc, deleteDoc, getDoc, setDoc
} from 'firebase/firestore';

import { db } from '../lib/firebase';
import { Link } from 'react-router-dom';
import HomepageManager from '../components/admin/HomepageManager';
import AdminDiscounts from '../layout/AdminDiscounts';

// ======================= ADMIN LOGIN =======================
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const ref = doc(db, "adminAuth", "config");
      const snap = await getDoc(ref);

      if (snap.exists()) {
        if (pin === snap.data().adminPin) setIsAuthenticated(true);
        else alert("Hatalı PIN!");
      } else {
        await setDoc(ref, { adminPin: "1234" });
        alert("İlk kurulum yapıldı. PIN: 1234");
      }
    } catch {
      alert("Bağlantı hatası!");
    }

    setLoading(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="h-screen bg-[#064e3b] flex items-center justify-center">
        <div className="bg-fecos-surface p-10 rounded-xl w-96 text-center shadow-xl">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 text-[#064e3b] flex items-center justify-center rounded-full">
            <Lock size={40} />
          </div>

          <h2 className="text-3xl font-serif font-bold text-[#064e3b]">
            FECOS <span className="text-[#d97706]">FOOD</span>
          </h2>
          <p className="text-gray-500 text-sm tracking-widest uppercase mb-6">Admin Paneli</p>

          <form onSubmit={handleLogin} className="space-y-6">
            <input
              type="password"
              placeholder="PIN Kodu"
              className="w-full text-center text-2xl p-3 border-b-2 border-[#064e3b] outline-none tracking-[0.5em]"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
            <button className="w-full bg-[#064e3b] text-white py-4 rounded-xl font-bold">
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <DashboardLayout />;
}

// ======================= DASHBOARD LAYOUT =======================
const DashboardLayout = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [products, setProducts] = useState([]);

  useEffect(() => {
    console.log("AdminPage: Subscribing to products...");
    const unsub = onSnapshot(
      collection(db, "products"),
      snap => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        console.log("AdminPage product fetch. Count:", data.length);
        setProducts(data);
      },
      error => {
        console.error("AdminPage product fetch error:", error);
      }
    );
    return () => unsub();
  }, []);

  return (
    <div className="flex h-screen bg-gray-50">

      {/* SIDEBAR */}
      <aside className="w-64 bg-[#064e3b] text-white flex flex-col shadow-xl">

        <div className="h-24 flex items-center justify-center border-b border-[#042f24]">
          <div className="text-center leading-none">
            <span className="text-2xl font-extrabold">
              FECOS<span className="text-[#d97706]">FOOD</span>
            </span>
            <p className="text-[10px] text-[#a7f3d0] mt-1 tracking-widest">
              Admin v2.0
            </p>
          </div>
        </div>

        <nav className="flex-1 py-6 px-4 space-y-2">
          <SidebarItem label="Genel Bakış" icon={<LayoutDashboard size={18} />} active={activeTab === "dashboard"} onClick={() => setActiveTab("dashboard")} />
          <SidebarItem label="Ürün Yönetimi" icon={<Package size={18} />} active={activeTab === "products"} onClick={() => setActiveTab("products")} />
          <SidebarItem label="İndirim Yönetimi" icon={<Tag size={18} />} active={activeTab === "discounts"} onClick={() => setActiveTab("discounts")} />

          {/* 🔥 Homepage Yönetimi */}
          <SidebarItem label="Homepage Yönetimi" icon={<Megaphone size={18} />} active={activeTab === "homepage"} onClick={() => setActiveTab("homepage")} />

          {/* ÜRÜN EKLE */}
          <Link
            to="/admin/add-product"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 hover:bg-[#042f24] hover:text-white transition-all"
          >
            <Plus size={18} />
            <span className="text-sm">Ürün Ekle</span>
          </Link>

          {/* TOPLU YÜKLEME */}
          <Link
            to="/admin/bulk-upload"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 hover:bg-[#042f24] hover:text-white transition-all"
          >
            <UploadCloud size={18} />
            <span className="text-sm">Toplu Yükle</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-[#042f24]">
          <button onClick={() => window.location.href = "/"} className="flex items-center gap-3 text-yellow-400 hover:text-white">
            <LogOut size={18} /> Siteye Dön
          </button>
        </div>

      </aside>

      {/* MAIN */}
      <main className="flex-1 flex flex-col overflow-hidden">

        <header className="h-20 bg-fecos-surface shadow px-8 flex items-center justify-between sticky top-0">
          <h2 className="text-2xl font-serif font-bold text-[#064e3b]">
            {activeTab === "dashboard" && "Panel Özeti"}
            {activeTab === "products" && "Ürün Kataloğu"}
            {activeTab === "homepage" && "Homepage Yönetimi"}
            {activeTab === "discounts" && "İndirim Yönetimi"}
          </h2>

          <div className="relative p-2 bg-gray-100 rounded-full cursor-pointer">
            <Bell size={18} />
            <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-white border"></span>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-8">
          {activeTab === "dashboard" && <DashboardHome products={products} />}
          {activeTab === "products" && <ProductsView products={products} />}
          {activeTab === "homepage" && <HomepageManager products={products} />}
          {activeTab === "discounts" && <AdminDiscounts />}
        </div>

      </main>
    </div>
  );
};

// ======================= SIDEBAR ITEM =======================
const SidebarItem = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all 
      ${active ? "bg-[#d97706] text-white font-bold" : "text-gray-300 hover:bg-[#042f24] hover:text-white"}
    `}
  >
    {icon}
    <span>{label}</span>
  </button>
);

// ======================= PRODUCTS LIST =======================
const ProductsView = ({ products }) => {
  return (
    <div className="space-y-10">
      <Link
        to="/admin/add-product"
        className="inline-flex items-center gap-2 bg-[#064e3b] text-white px-6 py-2 rounded-lg font-bold shadow-md hover:bg-[#042f24]"
      >
        <Plus size={18} /> Yeni Ürün Ekle
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {products.map(p => (
          <div key={p.id} className="bg-fecos-surface rounded-xl border shadow group">
            <div className="h-48 bg-gray-100 flex items-center justify-center relative">
              <img src={p.image} className="h-full object-contain" />

              <button
                className="absolute top-2 right-2 bg-red-50 text-red-600 p-2 rounded-full opacity-0 group-hover:opacity-100 shadow"
                onClick={() => deleteDoc(doc(db, "products", p.id))}
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="p-4">
              <p className="text-[10px] text-[#d97706] uppercase tracking-wider">{p.category}</p>
              <h4 className="font-bold text-gray-800">{p.name}</h4>
              <p className="text-xl font-bold text-[#064e3b]">{p.price} ₺</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ======================= ADMIN LOGIN =======================

// ======================= DASHBOARD HOME =======================
const DashboardHome = ({ products }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
    <div className="bg-fecos-surface p-6 rounded-xl shadow">
      <h4 className="text-lg font-bold text-[#064e3b]">Toplam Ürün</h4>
      <p className="text-4xl mt-2 font-black">{products.length}</p>
    </div>
  </div>
);
